import GATSBY_COMPILED_MDX from "/opt/build/repo/src/posts/rate-limiting-patterns.mdx";
import React from "react";
import {Link} from "gatsby";
import {MDXProvider} from "@mdx-js/react";
import {Helmet} from "react-helmet";
import Layout from "../components/layout";
import Topic from "../components/Topic";
import NewsletterSignup from "../components/NewsletterSignup";
import MermaidDiagram from "../components/MermaidDiagram";
import EmailOff from "../components/EmailOff";
import {PERSON_ID, SOCIAL} from "../constants/site";
const components = {
  MermaidDiagram,
  EmailOff,
  pre: props => React.createElement("div", Object.assign({}, props, {
    className: "overflow-auto"
  })),
  iframe: props => React.createElement("div", {
    className: "aspect-video"
  }, React.createElement("iframe", Object.assign({}, props, {
    className: "w-full h-full rounded-lg"
  })))
};
const PostTemplate = _ref => {
  let {data, children} = _ref;
  if (!data || !data.mdx) {
    return React.createElement(Layout, null, React.createElement("div", {
      className: "max-w-4xl mx-auto px-4 py-8"
    }, React.createElement("h1", {
      className: "text-4xl font-bold mb-8"
    }, "Post not found"), React.createElement("p", null, "Sorry, the post you're looking for doesn't exist.")));
  }
  const {frontmatter, fields} = data.mdx;
  const {title, publishedAt, publishedAtISO, summary, label, tags} = frontmatter;
  const {readingTimeMinutes} = fields;
  const {siteUrl} = data.site.siteMetadata;
  const canonicalUrl = `${siteUrl}${fields.slug}`;
  const slug = fields.slug.split("/").at(-2) || "";
  const ogFeatureImageUrl = `https://bx.ehosseini.info/?text=${slug}&complexity=6&format=webp&width=1200&height=675&title=${encodeURIComponent(title)}&avatar=1`;
  const featureImageUrl = `https://bx.ehosseini.info/?text=${slug}&complexity=6&format=webp&width=1200&height=675&align=bottom`;
  const relatedPosts = data.relatedPosts.nodes.filter(post => post.fields.slug !== fields.slug).slice(0, 3);
  const blogPosting = {
    "@type": "BlogPosting",
    headline: title,
    datePublished: publishedAtISO,
    dateModified: publishedAtISO,
    description: summary,
    author: {
      "@id": PERSON_ID
    },
    publisher: {
      "@id": PERSON_ID
    },
    mainEntityOfPage: canonicalUrl,
    keywords: tags === null || tags === void 0 ? void 0 : tags.join(", "),
    image: ogFeatureImageUrl,
    url: canonicalUrl
  };
  return React.createElement(Layout, {
    schemaExtra: [blogPosting]
  }, React.createElement(Helmet, null, React.createElement("title", null, title, " | ", data.site.siteMetadata.title), React.createElement("meta", {
    name: "description",
    content: summary
  }), React.createElement("meta", {
    name: "keywords",
    content: tags.join(", ")
  }), React.createElement("meta", {
    property: "og:type",
    content: "article"
  }), React.createElement("meta", {
    property: "og:url",
    content: canonicalUrl
  }), React.createElement("meta", {
    property: "og:title",
    content: title
  }), React.createElement("meta", {
    property: "og:description",
    content: summary
  }), React.createElement("meta", {
    property: "og:image",
    content: ogFeatureImageUrl
  }), React.createElement("meta", {
    property: "og:image:secure_url",
    content: ogFeatureImageUrl
  }), React.createElement("meta", {
    property: "og:image:alt",
    content: title
  }), React.createElement("meta", {
    property: "og:image:width",
    content: "1200"
  }), React.createElement("meta", {
    property: "og:image:height",
    content: "675"
  }), React.createElement("meta", {
    property: "og:site_name",
    content: data.site.siteMetadata.title
  }), React.createElement("meta", {
    property: "article:published_time",
    content: publishedAtISO
  }), React.createElement("meta", {
    name: "twitter:card",
    content: "summary_large_image"
  }), React.createElement("meta", {
    name: "twitter:site",
    content: SOCIAL.xHandle
  }), React.createElement("meta", {
    name: "twitter:creator",
    content: SOCIAL.xHandle
  }), React.createElement("meta", {
    name: "twitter:title",
    content: title
  }), React.createElement("meta", {
    name: "twitter:description",
    content: summary
  }), React.createElement("meta", {
    name: "twitter:image",
    content: ogFeatureImageUrl
  }), React.createElement("meta", {
    name: "twitter:image:alt",
    content: title
  }), React.createElement("link", {
    rel: "canonical",
    href: canonicalUrl
  })), React.createElement("div", {
    className: "relative w-full"
  }, React.createElement("div", {
    className: "post-hero-bg-layer absolute inset-0 z-0",
    style: {
      backgroundImage: `url(${featureImageUrl})`,
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center"
    },
    "aria-hidden": "true"
  }), React.createElement("header", {
    className: "relative z-10 mb-8 max-w-3xl mx-auto px-4 pt-8 pb-4"
  }, React.createElement("div", {
    className: "flex flex-col sm:flex-row sm:items-center gap-3 text-sm text-gray-900 mb-4"
  }, React.createElement("time", {
    className: "flex items-center"
  }, React.createElement("svg", {
    className: "w-4 h-4 mr-2",
    fill: "none",
    stroke: "currentColor",
    viewBox: "0 0 24 24"
  }, React.createElement("path", {
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 2,
    d: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
  })), publishedAt), React.createElement("span", {
    className: "flex items-center"
  }, React.createElement("svg", {
    className: "w-4 h-4 mr-2",
    fill: "none",
    stroke: "currentColor",
    viewBox: "0 0 24 24"
  }, React.createElement("path", {
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 2,
    d: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
  })), readingTimeMinutes, " min read"), label && React.createElement("span", {
    className: "flex items-center"
  }, React.createElement("svg", {
    className: "w-4 h-4 mr-2",
    fill: "none",
    stroke: "currentColor",
    viewBox: "0 0 24 24"
  }, React.createElement("path", {
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 2,
    d: "M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
  })), label)), React.createElement("div", {
    className: "mb-6"
  }, React.createElement("h1", {
    className: "text-3xl font-playfair font-medium text-gray-900 leading-tight mb-4"
  }, title)), React.createElement("div", {
    className: "mb-6"
  }, React.createElement("p", {
    className: "text-md leading-relaxed mb-4 text-gray-900"
  }, summary), React.createElement("div", {
    className: "flex flex-wrap gap-2"
  }, tags === null || tags === void 0 ? void 0 : tags.map(tag => React.createElement(Topic, {
    key: tag,
    tag: tag,
    className: "text-sm"
  })))))), React.createElement("article", {
    className: "max-w-3xl mx-auto px-4 pyb-8"
  }, React.createElement("div", {
    className: ""
  }, React.createElement("div", {
    className: "flex items-center justify-between mb-4"
  }, React.createElement("div", null), React.createElement("div", {
    className: "flex space-x-3"
  }, React.createElement("a", {
    href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(canonicalUrl)}&hashtags=${encodeURIComponent(tags.join(", "))}&image=${encodeURIComponent(featureImageUrl)}`,
    target: "_blank",
    rel: "noopener noreferrer",
    className: "inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
  }, React.createElement("svg", {
    className: "w-4 h-4 mr-1.5",
    fill: "currentColor",
    viewBox: "0 0 24 24"
  }, React.createElement("path", {
    d: "M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"
  })), "Share"), React.createElement("a", {
    href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonicalUrl)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(summary)}&image=${encodeURIComponent(featureImageUrl)}`,
    target: "_blank",
    rel: "noopener noreferrer",
    className: "inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
  }, React.createElement("svg", {
    className: "w-4 h-4 mr-1.5",
    fill: "currentColor",
    viewBox: "0 0 24 24"
  }, React.createElement("path", {
    d: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
  })), "Share"), React.createElement("button", {
    onClick: () => {
      navigator.clipboard.writeText(canonicalUrl);
    },
    className: "inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
  }, React.createElement("svg", {
    className: "w-4 h-4 mr-1.5",
    fill: "none",
    stroke: "currentColor",
    viewBox: "0 0 24 24"
  }, React.createElement("path", {
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 2,
    d: "M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
  })), "Copy Link")))), React.createElement("div", {
    className: "prose prose-md max-w-none  prose-headings:font-playfair prose-headings:font-medium  prose-a:text-blue-600 dark:prose-a:text-blue-400  prose-a:no-underline hover:prose-a:text-blue-800 dark:hover:prose-a:text-blue-300 prose-img:rounded-lg prose-img:shadow-md dark:prose-invert dark:prose-headings:text-gray-100 dark:prose-p:text-gray-200 dark:prose-strong:text-gray-100 dark:prose-code:text-gray-200 dark:prose-pre:bg-gray-800 dark:prose-blockquote:text-gray-300 dark:prose-li:text-gray-200 dark:prose-td:text-gray-200 dark:prose-th:text-gray-200"
  }, React.createElement(MDXProvider, {
    components: components
  }, children)), React.createElement("div", {
    className: "mt-16 pt-8 border-t border-gray-200 dark:border-gray-700"
  }, React.createElement(NewsletterSignup, null)), relatedPosts.length > 0 && React.createElement("div", {
    className: "mt-16 pt-8 border-t border-gray-200 dark:border-gray-700"
  }, React.createElement("h2", {
    className: "text-2xl font-playfair font-medium text-gray-900 dark:text-gray-100 mb-6"
  }, "Related Posts"), React.createElement("div", {
    className: "grid grid-cols-1 md:grid-cols-3 gap-6"
  }, relatedPosts.map(post => React.createElement(Link, {
    key: post.id,
    to: post.fields.slug,
    className: "block bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
  }, React.createElement("h3", {
    className: "text-lg font-playfair font-medium text-gray-900 dark:text-gray-100 mb-2"
  }, post.frontmatter.title), React.createElement("div", {
    className: "flex items-center text-sm text-gray-500 dark:text-gray-400 mb-2"
  }, React.createElement("span", null, post.frontmatter.publishedAt)), React.createElement("p", {
    className: "text-gray-600 dark:text-gray-300 text-sm line-clamp-2"
  }, post.frontmatter.summary), React.createElement("div", {
    className: "mt-3 flex flex-wrap gap-1"
  }, post.frontmatter.tags.map(tag => React.createElement(Topic, {
    key: tag,
    tag: tag,
    className: "text-xs"
  })))))))));
};
const query = "1713836410";
PostTemplate
export default function GatsbyMDXWrapper(props) {
  return React.createElement(PostTemplate, props, React.createElement(GATSBY_COMPILED_MDX, props));
}
